import { useEffect, useState } from "react";

import { KanaDarkLogo, KanaLightLogo } from "../../../assets";
import { OrganisationRoleConstants, OrganisationTypeConstants } from "../../../constants";
import { useAuth } from "../../../useAuth";
import {
  getActivitiesRoute,
  getDashboardRoute,
  getInventoryRoute,
  getOperationsCompaniesRoute,
  getProjectsRoute,
  getSettingsRoute,
  getWorkflowTasksRoute,
  getWorkflowTemplatesRoute,
} from "../../../utils/routes";
import {
  DashboardGridIcon,
  EditIcon,
  FileTextIcon,
  LayersIcon,
  SettingsGearIcon,
  UserIcon,
  WorkflowIcon,
} from "./assets";
import { LeftNavigationBarPersona, NavigationBarItems } from "./models";

interface UseLeftNavigationBarParams {
  /** Key of the currently active item or sub-item. */
  activeItem?: string;
  /** Key of the group expanded on first render (accordion: one open at a time). */
  defaultExpandedKey?: string;
}

interface UseLeftNavigationBarReturnData {
  /** Persona variant driving the colour theme, derived from the signed-in user type. */
  persona: LeftNavigationBarPersona;
  /** Navigation items for the current persona, with real role-aware routes. */
  items: NavigationBarItems;
  /** Brand logo rendered at the top, themed for the persona. */
  navigationLogo: JSX.Element;
  /** Key of the currently expanded group (accordion state). */
  expandedKey?: string;
  /** Toggle a group open/closed; opening one closes any other. */
  toggleGroup: (key: string) => void;
  /** Whether the given item key is the active one. */
  isActive: (key: string) => boolean;
}

export const useLeftNavigationBar = ({
  activeItem,
  defaultExpandedKey,
}: UseLeftNavigationBarParams): UseLeftNavigationBarReturnData => {
  const { currentUserType, currentUserRole } = useAuth();

  const navigationLogo: Record<LeftNavigationBarPersona, JSX.Element> = {
    // Light personas use the dark wordmark; dark personas use the light one.
    Developer: <KanaLightLogo className="LeftNavigationBar_logo" />,
    Verifier: <KanaLightLogo className="LeftNavigationBar_logo" />,
    DevelopmentManager: <KanaDarkLogo className="LeftNavigationBar_logo" />,
  };

  const navigationItems: Record<LeftNavigationBarPersona, NavigationBarItems> = {
    Developer: [
      {
        key: "Dashboard",
        label: "Dashboard",
        icon: <DashboardGridIcon />,
        route: getDashboardRoute(OrganisationTypeConstants.DEVELOPER),
      },
      {
        key: "Projects",
        label: "Projects",
        icon: <FileTextIcon />,
        route: getProjectsRoute(OrganisationTypeConstants.DEVELOPER),
      },
      {
        key: "Inventory",
        label: "Inventory",
        icon: <LayersIcon />,
        route: getInventoryRoute(OrganisationTypeConstants.DEVELOPER),
      },
      {
        key: "Activities",
        label: "Activities",
        icon: <EditIcon />,
        route: getActivitiesRoute(OrganisationTypeConstants.DEVELOPER),
      },
      {
        key: "Workflows",
        label: "Workflows",
        icon: <WorkflowIcon />,
        children: [
          {
            key: "Templates",
            label: "Templates",
            route: getWorkflowTemplatesRoute(OrganisationTypeConstants.DEVELOPER),
          },
          {
            key: "Tasks",
            label: "Tasks",
            route: getWorkflowTasksRoute(OrganisationTypeConstants.DEVELOPER),
          },
        ],
      },
      // TODO: add "CRM" item (UserIcon) once a CRM route helper exists in routes.ts.
      {
        key: "CRM",
        label: "CRM",
        icon: <UserIcon />,
        route: getOperationsCompaniesRoute(OrganisationTypeConstants.DEVELOPER), // TODO: replace with CRM route once it exists
      },
    ],
    DevelopmentManager: [
      {
        key: "Dashboard",
        label: "Dashboard",
        icon: <DashboardGridIcon />,
        route: getDashboardRoute(OrganisationTypeConstants.DEVELOPMENT_MANAGER),
      },
      {
        key: "Projects",
        label: "Projects",
        icon: <FileTextIcon />,
        route: getProjectsRoute(OrganisationTypeConstants.DEVELOPMENT_MANAGER),
      },
      {
        key: "Inventory",
        label: "Inventory",
        icon: <LayersIcon />,
        route: getInventoryRoute(OrganisationTypeConstants.DEVELOPMENT_MANAGER),
      },
      {
        key: "Activities",
        label: "Activities",
        icon: <EditIcon />,
        route: getActivitiesRoute(OrganisationTypeConstants.DEVELOPMENT_MANAGER),
      },
      {
        key: "Workflows",
        label: "Workflows",
        icon: <WorkflowIcon />,
        children: [
          {
            key: "Templates",
            label: "Templates",
            route: getWorkflowTemplatesRoute(OrganisationTypeConstants.DEVELOPMENT_MANAGER),
          },
          {
            key: "Tasks",
            label: "Tasks",
            route: getWorkflowTasksRoute(OrganisationTypeConstants.DEVELOPMENT_MANAGER),
          },
        ],
      },
      // TODO: add "CRM" item (UserIcon) once a CRM route helper exists in routes.ts.
      {
        key: "CRM",
        label: "CRM",
        icon: <UserIcon />,
        route: getOperationsCompaniesRoute(OrganisationTypeConstants.DEVELOPMENT_MANAGER), // TODO: replace with CRM route once it exists
      },
      {
        key: "Settings",
        label: "Settings",
        icon: <SettingsGearIcon />,
        route: getSettingsRoute(OrganisationTypeConstants.DEVELOPMENT_MANAGER),
      },
    ],
    // VVB / Verifier has no Workflows (no verifier workflow route) and no CRM.
    Verifier: [
      {
        key: "Dashboard",
        label: "Dashboard",
        icon: <DashboardGridIcon />,
        route: getDashboardRoute(OrganisationTypeConstants.VERIFIER),
      },
      {
        key: "Projects",
        label: "Projects",
        icon: <FileTextIcon />,
        route: getProjectsRoute(OrganisationTypeConstants.VERIFIER),
      },
      // {
      //   key: "Inventory",
      //   label: "Inventory",
      //   icon: <LayersIcon />,
      //   route: getInventoryRoute(OrganisationTypeConstants.VERIFIER),
      // },
      // {
      //   key: "Activities",
      //   label: "Activities",
      //   icon: <EditIcon />,
      //   route: getActivitiesRoute(OrganisationTypeConstants.VERIFIER),
      // },
      {
        key: "Settings",
        label: "Settings",
        icon: <SettingsGearIcon />,
        route: getSettingsRoute(OrganisationTypeConstants.VERIFIER),
      },
    ],
  };

  if (currentUserRole === OrganisationRoleConstants.OWNER || currentUserRole === OrganisationRoleConstants.ADMIN) {
    navigationItems.Developer.push({
      key: "Settings",
      label: "Settings",
      icon: <SettingsGearIcon />,
      route: getSettingsRoute(OrganisationTypeConstants.DEVELOPER),
    });
  }

  // The bar only themes the three in-scope personas; other user types fall back to Developer.
  const persona: LeftNavigationBarPersona =
    currentUserType in navigationItems ? (currentUserType as LeftNavigationBarPersona) : "Developer";

  /**
   * The group holding the active sub-item. A page under a group (Workflows › Templates) would
   * otherwise land on a collapsed group with its active child hidden, so the group opens itself
   * rather than every such page having to pass `defaultExpandedKey` alongside `activeItem`.
   */
  const activeGroupKey = navigationItems[persona].find((item) =>
    item.children?.some((child) => child.key === activeItem)
  )?.key;

  const [expandedKey, setExpandedKey] = useState<string | undefined>(defaultExpandedKey ?? activeGroupKey);

  // Re-opens the group when the active item moves into a different one without the bar remounting.
  useEffect(() => {
    if (activeGroupKey) {
      setExpandedKey(activeGroupKey);
    }
  }, [activeGroupKey]);

  const toggleGroup = (key: string): void => {
    // Accordion, toggle-only: opening one group closes any other.
    setExpandedKey((current) => (current === key ? undefined : key));
  };

  const isActive = (key: string): boolean => key === activeItem;

  return {
    persona,
    items: navigationItems[persona],
    navigationLogo: navigationLogo[persona],
    expandedKey,
    toggleGroup,
    isActive,
  };
};
